import { Injectable } from '@angular/core';
import { QuestionHttpModel, QuestionItemV2 } from '@examdojo/models/question';
import { Feedback } from '@examdojo/question';
import { ReferenceQuestionHttpModel, ReferenceQuestionHttpService } from '@examdojo/reference-question';
import { DbJson, ExamdojoSupabaseService, SupabaseBaseHttpService } from '@examdojo/supabase';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DerivativeQuestionFeedback, ReferenceQuestionFeedback } from './review-item.model';

@Injectable({ providedIn: 'root' })
export class ReviewItemHttpService extends SupabaseBaseHttpService<'reviewable_items', 'review'> {
  constructor(
    protected override readonly supabase: ExamdojoSupabaseService,
    private readonly referenceQuestionHttpService: ReferenceQuestionHttpService,
  ) {
    super(supabase);
  }

  readonly schema = 'review';
  readonly tableName = 'reviewable_items';

  giveQuestionFeedback(questionId: QuestionHttpModel['id'], feedback: Feedback[]) {
    return from(
      this.supabase.client
        .schema('review')
        .rpc('create_question_review_action', {
          p_feedback: feedback as unknown as DbJson,
          p_question_id: questionId,
        })
        .select('*')
        .throwOnError(),
    );
  }

  giveReferenceQuestionFeedback(referenceQuestionId: ReferenceQuestionHttpModel['id'], feedback: Feedback[]) {
    return from(
      this.supabase.client
        .schema('review')
        .rpc('create_reference_question_review_action', {
          p_feedback: feedback as unknown as DbJson,
          p_reference_question_id: referenceQuestionId,
        })
        .select('*')
        .throwOnError(),
    );
  }

  /**
   * Retrieves all feedback for a specific reference question with snapshot data
   */
  getReferenceQuestionFeedback(
    referenceQuestionId: ReferenceQuestionHttpModel['id'],
  ): Observable<ReferenceQuestionFeedback[]> {
    return from(
      this.supabase.client
        .schema('review')
        .rpc('get_reference_question_feedback_with_snapshot', {
          p_reference_question_id: referenceQuestionId,
        })
        .throwOnError(),
    ).pipe(
      map((response) => {
        return response.data.map((d) => ({
          ...d,
          question_items: d.question_items as unknown as QuestionItemV2[],
          feedback: d.feedback as unknown as Feedback[],
        }));
      }),
    );
  }

  /**
   * Retrieves all feedback for a specific derivative question with snapshot data
   * @param questionId - The ID of the derivative question
   * @returns Observable with array of feedback responses with question snapshots, ordered by creation date (newest first)
   */
  getQuestionFeedback(questionId: QuestionHttpModel['id']): Observable<DerivativeQuestionFeedback[]> {
    return from(
      this.supabase.client
        .schema('review')
        .rpc('get_question_feedback_with_snapshot', {
          p_question_id: questionId,
        })
        .throwOnError(),
    ).pipe(
      map((response) => {
        return response.data.map((d) => ({
          action_id: d.action_id,
          feedback: d.feedback as unknown as Feedback[],
          action_type: d.action_type,
          user_id: d.user_id,
          user_first_name: d.user_first_name,
          user_last_name: d.user_last_name,
          user_email: d.user_email,
          created_at: d.created_at,
          updated_at: d.updated_at,
          question_items: d.question_items as unknown as QuestionItemV2[],
        }));
      }),
    );
  }

  fetchByQuestionId(questionId: QuestionHttpModel['id']) {
    return from(
      this.query()
        .select()
        .eq('question_id', questionId)
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  fetchByReferenceQuestionId(referenceQuestionId: ReferenceQuestionHttpModel['id']) {
    return from(
      this.query()
        .select()
        .eq('reference_question_id', referenceQuestionId)
        .throwOnError()
        .then((response) => response.data!),
    );
  }
}
